import { LocationStrategy } from '@angular/common';
import { Component, HostBinding, HostListener, Input } from '@angular/core';
import type { UrlTree } from '@angular/router';
import { ActivatedRoute, Router } from '@angular/router';
import { MuiIconFont } from '@move/ui';
import { MmuUserType } from '../../auth/models';


@Component({
  // The component should use an <a> tag for accessibility reasons
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'a[mmuMainMenuLink]',
  template: `
    <mui-avatar class="avatar" *ngIf="avatar" [name]="avatar" [size]="'xs'" [avatarSubIcon]="userType === 'service' ? 'wrench' : ''"></mui-avatar>
    <i *ngIf="icon" mui-icon [font]="iconFont" [name]="icon" class="menu-icon"></i>
    <span>
      <ng-content></ng-content>
    </span>
  `
})
export class MmuMainMenuLink {
  @Input()
  icon = '';

  @Input()
  avatar = '';

  @Input()
  userType: MmuUserType = 'customer';

  @Input()
  iconFont: MuiIconFont | string = MuiIconFont.FONTAWESOME;

  @HostBinding('class.mui-main-navigation__link')
  _muiBaseClass = true;

  @Input()
  isExternalLink = false;

  @HostBinding('attr.target')
  @Input()
  target: string | null = null;

  @HostBinding('attr.href')
  href: string | null = null;

  @Input() set mmuMainMenuLink(url: string) {
    this.urlTree = this.getInternalUrlTree(url);
    this.href = url ? url : null;
  }

  urlTree: UrlTree | null = null;

  constructor(private _router: Router, private _route: ActivatedRoute, private _locationStrategy: LocationStrategy) {
  }

  @HostListener('click', ['$event.button', '$event.ctrlKey', '$event.metaKey', '$event.shiftKey'])
  onClick(button: number, ctrlKey: boolean, metaKey: boolean, shiftKey: boolean): boolean {
    /* Do not use router navigation when the link should be treated as external */
    if (!this.urlTree || this.isExternalLink) {
      return true;
    }

    /* Do not use router navigation when not the left mouse button or any modifier was used */
    if (button !== 0 || ctrlKey || metaKey || shiftKey) {
      return true;
    }

    /* Do not use router navigation when the link target is not _self */
    if (typeof this.target === 'string' && this.target !== '_self') {
      return true;
    }

    // if internal navigation is happening the scrollbar must be enabled again
    // this.scrollbarService.updateBodyScrollBar();
    this._router.navigateByUrl(this.urlTree).then();
    return false;
  }

  /**
   * Prepare the provided URL and transform it into an UrlTree. For internal links, the baseHref in the URL is removed.
   *
   * @param url The absolute URL of the link.
   */
  private getInternalUrlTree(url: string): UrlTree | null {
    /* eslint-disable-next-line no-extra-boolean-cast */
    if (!!url) {
      if (!this.isExternalLink) {
        // Prepare internal URL by removing the baseHref
        url = url.replace(this._locationStrategy.getBaseHref(), '/');
      }
      return this._router.parseUrl(url);
    } else {
      return null;
    }
  }
}
